import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "Redux/store";
import {
  RecordListStatus,
  finishRecordList,
  idleRecordList,
  startRecordList,
} from "Redux/recordList";
import {
  IAssignmentHistoryItem,
  IEquipDetails,
  IEquipItem,
  IEquipType,
  ILinkedVehicleItem,
  IRentHistoryItem,
  IUser,
  IVehicle,
} from "Shared/Types/equipment";
import { format } from "Shared/Utils/shared";
import {
  addEquipment,
  addRent,
  assignEquipment,
  deleteEquipment,
  deleteRent,
  getAssignedVehicles,
  getAssignmentsHistory,
  getEquipmentDetails,
  getEquipmentList,
  getEquipmentTypes,
  getLinkedVehiclesHistory,
  getRentHistory,
  getUsers,
  getVehicles,
  unassignEquipment,
  updateEquipment,
  updateRent,
  updateRentToPaid,
  updateRentToUnpaid,
} from "./reducer";

interface IEquipmentState {
  /** Params of the last table request, reused to reload after edits. */
  lastListParams: Parameters<typeof getEquipmentList>[0] | null;
  isFuncLoading: boolean;
  isFuncLoadingUnpaid: boolean;
  isFuncLoadingPaid: boolean;
  equipmentTypes: {
    loading: boolean;
    items: IEquipType[];
    numPage: number;
  };
  assignedVehicles: {
    items: IVehicle[];
  };
  vehicles: {
    items: IVehicle[];
  };
  users: {
    items: IUser[];
  };
  equipmentList: {
    loading: boolean;
    items: IEquipItem[];
    count: number;
  };
  equipmentHistory: {
    linkedVehicles: EquipmentHistoryList<ILinkedVehicleItem>;
    assignments: EquipmentHistoryList<IAssignmentHistoryItem>;
    rent: EquipmentHistoryList<IRentHistoryItem>;
  };
  equipmentDetails: Partial<IEquipDetails>;
  loading: boolean;
}

export type EquipmentHistoryKey = "linkedVehicles" | "assignments" | "rent";
export type EquipmentHistories = IEquipmentState["equipmentHistory"];

export type EquipmentHistoryList<T> = RecordListStatus & {
  items: T[];
  count: number;
};

const idleHistory = <T>(): EquipmentHistoryList<T> => ({
  ...idleRecordList(),
  items: [],
  count: 0,
});

const emptyHistory = () => ({
  linkedVehicles: idleHistory<ILinkedVehicleItem>(),
  assignments: idleHistory<IAssignmentHistoryItem>(),
  rent: idleHistory<IRentHistoryItem>(),
});

// A different equipment, page or search clears the rows; a refresh keeps them.
const startHistory = (
  list: EquipmentHistoryList<unknown>,
  meta: { requestId: string; arg: { id: string } },
) => {
  if (startRecordList(list, meta)) {
    list.items = [];
    list.count = 0;
  }
};

const finishHistory = finishRecordList;

const initialState: IEquipmentState = {
  lastListParams: null,
  isFuncLoading: false,
  isFuncLoadingUnpaid: false,
  isFuncLoadingPaid: false,
  equipmentTypes: {
    loading: false,
    items: [],
    numPage: 0,
  },
  assignedVehicles: {
    items: [],
  },
  vehicles: {
    items: [],
  },
  users: {
    items: [],
  },
  equipmentList: {
    loading: false,
    items: [],
    count: 0,
  },
  equipmentHistory: emptyHistory(),
  equipmentDetails: {},
  loading: false,
};

const equipmentSlice = createSlice({
  name: "equipment",
  initialState,
  reducers: {
    /** Forgets the open equipment and its history lists. */
    clearCurrentEquipment(state) {
      state.equipmentDetails = {};
      state.equipmentHistory = emptyHistory();
    },
    toggleEquipItemAssignStatus(
      state,
      action: PayloadAction<{
        equipmentID: string;
        vehicleID?: string;
        userID?: string;
      }>,
    ) {
      const equipmentID = action.payload.equipmentID;
      const itemToToggle = state.equipmentList.items.find(
        (item) => item.id === equipmentID,
      );

      if (itemToToggle) {
        if (itemToToggle.is_assigned === true) {
          itemToToggle.is_assigned = false;
          itemToToggle.linked_vehicle = null;
          itemToToggle.assigned_user = null;
          itemToToggle.start_date = null;
        } else {
          const vehicleID = action.payload.vehicleID;
          const linkedVehicle = state.vehicles.items.find(
            (item) => item.id === vehicleID,
          );
          const userID = action.payload.userID;
          const assignedUser = state.users.items.find(
            (item) => item.id === userID,
          );

          itemToToggle.is_assigned = true;
          itemToToggle.linked_vehicle =
            itemToToggle.linked_vehicle && linkedVehicle
              ? { ...itemToToggle.linked_vehicle, ...linkedVehicle }
              : null;
          itemToToggle.assigned_user = assignedUser || null;
          itemToToggle.start_date = format(new Date(), "YYYY-MM-DD");
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getEquipmentTypes.pending, (state) => {
      state.equipmentTypes.loading = true;
      state.equipmentTypes.items = [];
      state.equipmentTypes.numPage = 0;
    });
    builder.addCase(getEquipmentTypes.fulfilled, (state, action) => {
      state.equipmentTypes.loading = false;
      state.equipmentTypes.items = action.payload.results || [];
      state.equipmentTypes.numPage = action.payload.count || 0;
    });
    builder.addCase(getEquipmentTypes.rejected, (state) => {
      state.equipmentTypes.loading = false;
      state.equipmentTypes.numPage = 0;
    });

    builder.addCase(getAssignedVehicles.pending, (state) => {
      state.loading = true;
      state.assignedVehicles.items = [];
    });
    builder.addCase(getAssignedVehicles.fulfilled, (state, action) => {
      state.loading = false;
      state.assignedVehicles.items = action.payload.results || [];
    });
    builder.addCase(getAssignedVehicles.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getVehicles.pending, (state) => {
      state.loading = true;
      state.vehicles.items = [];
    });
    builder.addCase(getVehicles.fulfilled, (state, action) => {
      state.loading = false;
      state.vehicles.items = action.payload.results || [];
    });
    builder.addCase(getVehicles.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getUsers.pending, (state) => {
      state.loading = true;
      state.users.items = [];
    });
    builder.addCase(getUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.users.items = action.payload.results || [];
    });
    builder.addCase(getUsers.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getEquipmentList.pending, (state, action) => {
      if (action.meta.arg) state.lastListParams = action.meta.arg;
      state.equipmentList.loading = true;
      state.equipmentList.items = [];
      state.equipmentList.count = 0;
    });
    builder.addCase(getEquipmentList.fulfilled, (state, action) => {
      state.equipmentList.loading = false;
      state.equipmentList.items = action.payload.results || [];
      state.equipmentList.count = action.payload.count || 0;
    });
    builder.addCase(getEquipmentList.rejected, (state) => {
      state.equipmentList.loading = false;
    });

    builder.addCase(getLinkedVehiclesHistory.pending, (state, action) => {
      startHistory(state.equipmentHistory.linkedVehicles, action.meta);
    });
    builder.addCase(getLinkedVehiclesHistory.fulfilled, (state, action) => {
      const list = state.equipmentHistory.linkedVehicles;
      if (finishHistory(list, action.meta.requestId)) {
        list.items = action.payload.results || [];
        list.count = action.payload.count || 0;
      }
    });
    builder.addCase(getLinkedVehiclesHistory.rejected, (state, action) => {
      finishHistory(
        state.equipmentHistory.linkedVehicles,
        action.meta.requestId,
      );
    });

    builder.addCase(getAssignmentsHistory.pending, (state, action) => {
      startHistory(state.equipmentHistory.assignments, action.meta);
    });
    builder.addCase(getAssignmentsHistory.fulfilled, (state, action) => {
      const list = state.equipmentHistory.assignments;
      if (finishHistory(list, action.meta.requestId)) {
        list.items = action.payload.results || [];
        list.count = action.payload.count || 0;
      }
    });
    builder.addCase(getAssignmentsHistory.rejected, (state, action) => {
      finishHistory(state.equipmentHistory.assignments, action.meta.requestId);
    });

    builder.addCase(getRentHistory.pending, (state, action) => {
      startHistory(state.equipmentHistory.rent, action.meta);
    });
    builder.addCase(getRentHistory.fulfilled, (state, action) => {
      const list = state.equipmentHistory.rent;
      if (finishHistory(list, action.meta.requestId)) {
        list.items = action.payload.results || [];
        list.count = action.payload.count || 0;
      }
    });
    builder.addCase(getRentHistory.rejected, (state, action) => {
      finishHistory(state.equipmentHistory.rent, action.meta.requestId);
    });

    builder.addCase(getEquipmentDetails.pending, (state) => {
      state.loading = true;
      state.equipmentDetails = {};
    });
    builder.addCase(getEquipmentDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.equipmentDetails = action.payload;
    });
    builder.addCase(getEquipmentDetails.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(unassignEquipment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(unassignEquipment.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(unassignEquipment.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(assignEquipment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(assignEquipment.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(assignEquipment.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteEquipment.pending, (state) => {
      state.loading = true;
      state.isFuncLoading = true;
    });
    builder.addCase(deleteEquipment.rejected, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });
    builder.addCase(deleteEquipment.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });

    builder.addCase(addEquipment.pending, (state) => {
      state.loading = true;
      state.isFuncLoading = true;
    });
    builder.addCase(addEquipment.rejected, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });
    builder.addCase(addEquipment.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });

    builder.addCase(updateEquipment.pending, (state) => {
      state.loading = true;
      state.isFuncLoading = true;
    });
    builder.addCase(updateEquipment.rejected, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });
    builder.addCase(updateEquipment.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });

    builder.addCase(addRent.pending, (state) => {
      state.loading = true;
      state.isFuncLoading = true;
    });
    builder.addCase(addRent.rejected, (state) => {
      state.loading = false;
      state.isFuncLoading = false;
    });
    builder.addCase(addRent.fulfilled, (state) => {
      state.loading = false;
      state.isFuncLoading = false;
    });

    builder.addCase(deleteRent.pending, (state) => {
      state.loading = true;
      state.isFuncLoading = true;
    });
    builder.addCase(deleteRent.rejected, (state) => {
      state.loading = false;
      state.isFuncLoading = false;
    });
    builder.addCase(deleteRent.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.loading = false;
    });

    builder.addCase(updateRent.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateRent.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateRent.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(updateRentToPaid.pending, (state) => {
      state.loading = true;
      state.isFuncLoadingPaid = true;
    });
    builder.addCase(updateRentToPaid.rejected, (state) => {
      state.loading = false;
      state.isFuncLoadingPaid = false;
    });
    builder.addCase(updateRentToPaid.fulfilled, (state) => {
      state.isFuncLoadingPaid = false;
      state.loading = false;
    });

    builder.addCase(updateRentToUnpaid.pending, (state) => {
      state.loading = true;
      state.isFuncLoadingUnpaid = true;
    });
    builder.addCase(updateRentToUnpaid.rejected, (state) => {
      state.loading = false;
      state.isFuncLoadingUnpaid = false;
    });
    builder.addCase(updateRentToUnpaid.fulfilled, (state) => {
      state.loading = false;
      state.isFuncLoadingUnpaid = false;
    });
  },
});

export const { toggleEquipItemAssignStatus, clearCurrentEquipment } =
  equipmentSlice.actions;

export const selectEquipmentTypesLoading = (state: RootState) =>
  state.equipment.equipmentTypes.loading;
export const selectEquipmentTypes = (state: RootState) =>
  state.equipment.equipmentTypes.items;
export const selectEquipmentTypesNumPage = (state: RootState) =>
  state.equipment.equipmentTypes.numPage;
export const selectAssignedVehicles = (state: RootState) =>
  state.equipment.assignedVehicles.items;
export const selectVehicles = (state: RootState) =>
  state.equipment.vehicles.items;
export const selectUsers = (state: RootState) => state.equipment.users.items;
export const selectEquipmentListLoading = (state: RootState) =>
  state.equipment.equipmentList.loading;
export const selectEquipmentList = (state: RootState) =>
  state.equipment.equipmentList.items;
export const selectEquipmentListCount = (state: RootState) =>
  state.equipment.equipmentList.count;
export const selectEquipmentHistory =
  <K extends EquipmentHistoryKey>(key: K) =>
  (state: RootState) =>
    state.equipment.equipmentHistory[key];
export const selectAssignmentsHistory = (state: RootState) =>
  state.equipment.equipmentHistory.assignments.items;
export const selectEquipmentDetails = (state: RootState) =>
  state.equipment.equipmentDetails;
export const selectEquipmentLoading = (state: RootState) =>
  state.equipment.loading;
export const selectEquipmentIsFuncLoading = (state: RootState) =>
  state.equipment.isFuncLoading;
export const selectEquipmentIsFuncLoadingUnpaid = (state: RootState) =>
  state.equipment.isFuncLoadingUnpaid;
export const selectEquipmentIsFuncLoadingPaid = (state: RootState) =>
  state.equipment.isFuncLoadingPaid;

export const selectEquipmentListLastParams = (state: RootState) =>
  state.equipment.lastListParams;

export default equipmentSlice;
